import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getEnv } from '../../config/Env';
import { baseQueryWithReauth, isJsonContentType, prepareHeaders, providesList } from '../api';

const baseUrl = getEnv('FORM_API_BASE_URL');
const uriPrefix = '/api';

const formApiQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders,
    isJsonContentType,
    responseHandler: 'content-type',
});

export const formApi = createApi({
    reducerPath: 'formApi',
    baseQuery: (args, api, extraOptions) => baseQueryWithReauth(args, api, extraOptions, formApiQuery),
    tagTypes: ['Form', 'Submission', 'FormGroup'],
    endpoints: (builder) => ({
        getForms: builder.query({
            query: () => `${uriPrefix}/forms`,
            providesTags: (result) => providesList(result['hydra:member'], 'Form', '@id'),
        }),
        getForm: builder.query({
            query: (id) => `${uriPrefix}/forms/${id}`,
            providesTags: (result) => [{ type: 'Form', id: result['@id'] }],
        }),
        addForm: builder.mutation({
            query: (body) => ({
                url: `${uriPrefix}/forms`,
                method: 'POST',
                body,
            }),
            invalidatesTags: () => [{ type: 'Form' }, { type: 'FormGroup' }],
        }),
        updateForm: builder.mutation({
            query: ({ uri, body }) => ({
                url: uri,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Form', id: arg.uri },
                { type: 'FormGroup', id: 'LIST' },
            ],
        }),
        patchForm: builder.mutation({
            query: ({ uri, body }) => ({
                url: uri,
                method: 'PATCH',
                headers: { 'Content-Type': 'application/merge-patch+json' },
                body,
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Form', id: arg.uri },
                { type: 'FormGroup', id: 'LIST' },
            ],
        }),
        deleteForm: builder.mutation({
            query: (uri) => ({
                url: uri,
                method: 'DELETE',
            }),
            invalidatesTags: () => [{ type: 'Form' }],
        }),
        getSubmissions: builder.query({
            query: ({ id, page }) => `${uriPrefix}/forms/${id}/submissions?page=${page}`,
            providesTags: (result) => providesList(result['hydra:member'], 'Submission', '@id'),
        }),
        getSubmission: builder.query({
            query: (id) => `${uriPrefix}/submissions/${id}`,
            providesTags: (result) => [{ type: 'Submission', id: result['@id'] }],
        }),
        getFormGroups: builder.query({
            query: () => `${uriPrefix}/form_groups`,
            transformResponse: (response) => response['hydra:member'],
            keepUnusedDataFor: 3600, // cache data 1 hour
            providesTags: (result) => providesList(result['hydra:member'], 'FormGroup'),
        }),
        createFormGroup: builder.mutation({
            query: (body) => ({
                url: `${uriPrefix}/form_groups`,
                method: 'POST',
                body,
            }),
            invalidatesTags: () => [{ type: 'FormGroup' }],
        }),
        updateFormGroup: builder.mutation({
            query: ({ uri, body }) => ({
                url: uri,
                method: 'PATCH',
                headers: { 'Content-Type': 'application/merge-patch+json' },
                body,
            }),
            invalidatesTags: (_result, _error, { uri }) => [{ type: 'FormGroup', uri }],
        }),
        deleteFormGroup: builder.mutation({
            query: (uri) => ({
                url: uri,
                method: 'DELETE',
            }),
            invalidatesTags: () => [{ type: 'FormGroup' }],
        }),
    }),
});

export const {
    useGetFormsQuery,
    useGetFormQuery,
    useAddFormMutation,
    useUpdateFormMutation,
    usePatchFormMutation,
    useGetSubmissionsQuery,
    useGetSubmissionQuery,
    useGetFormGroupsQuery,
    useCreateFormGroupMutation,
    useUpdateFormGroupMutation,
    useDeleteFormGroupMutation,
    useDeleteFormMutation,
} = formApi;
